import React, { useEffect, useState } from "react";
import { ArrowLeft, MessageSquare, Trash2 } from "lucide-react";
import { toast } from "sonner";
import client, { API, apiErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { AiText, Spinner } from "../ui-kit";
import { stamp } from "./ChartFeed";

const IMG = (u) => API.replace(/\/api$/, "") + u;

function Comments({ post, reload }) {
  const { user } = useAuth();
  const [text, setText] = useState("");
  const send = async () => {
    if (!text.trim()) return;
    try { await client.post(`/blog/${post.id}/comments`, { text }); setText(""); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const del = async (id) => { try { await client.delete(`/blog-comments/${id}`); reload(); } catch (e) { toast.error(apiErr(e)); } };
  const admin = user && !["client", "team_member"].includes(user.role);
  return (
    <section className="mt-8" data-testid="blog-comments">
      <h3 className="font-head text-white flex items-center gap-2"><MessageSquare size={16} className="text-gold" />Comments ({post.comment_list.length})</h3>
      <div className="mt-3 space-y-2">
        {post.comment_list.map((c) => (
          <div key={c.id} className="surface px-4 py-3" data-testid={`comment-${c.id}`}>
            <div className="flex items-center gap-2 text-xs"><span className="text-white font-semibold">{c.name}</span>{c.role === "team_member" && <span className="text-gold">Partner</span>}<span className="text-slate-500">{stamp(c.created_at)}</span>
              {user && (c.user_id === user.id || admin) && <button data-testid={`comment-delete-${c.id}`} onClick={() => del(c.id)} className="ml-auto text-slate-500 hover:text-bear"><Trash2 size={13} /></button>}
            </div>
            <p className="text-sm text-slate-300 mt-1 whitespace-pre-line">{c.text}</p>
          </div>
        ))}
      </div>
      {user ? (
        <div className="mt-3 flex gap-2">
          <input data-testid="comment-input" value={text} onChange={(e) => setText(e.target.value)} onKeyDown={(e) => e.key === "Enter" && send()} placeholder="Apna comment likhein…" className="flex-1 bg-transparent border border-[color:var(--border-subtle)] px-3 py-2 text-sm text-white focus:border-gold outline-none" />
          <button data-testid="comment-send" onClick={send} className="btn-gold px-4 text-sm">Post</button>
        </div>
      ) : <p className="text-xs text-slate-500 mt-3"><a href="/login" className="text-gold">Login</a> karke comment karein.</p>}
    </section>
  );
}

export function BlogPost({ slug, onBack }) {
  const [p, setP] = useState(null);
  const load = () => client.get(`/blog/${slug}`).then(({ data }) => setP(data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!p) return <Spinner label="Loading post" />;
  return (
    <article className="max-w-3xl" data-testid="blog-post">
      {onBack && <button data-testid="blog-back" onClick={onBack} className="text-xs text-slate-400 hover:text-white flex items-center gap-1 mb-4"><ArrowLeft size={14} />All posts</button>}
      <div className="text-[11px] text-slate-500">{stamp(p.created_at)} · {p.author}</div>
      <h1 className="font-head text-3xl sm:text-4xl text-white font-bold mt-2 leading-tight">{p.title}</h1>
      {p.image_url && <img src={IMG(p.image_url)} alt="" className="w-full mt-5 bg-black max-h-[460px] object-contain" />}
      <div className="mt-5"><AiText text={p.body} /></div>
      <Comments post={p} reload={load} />
    </article>
  );
}

export function BlogList({ onOpen }) {
  const [rows, setRows] = useState(null);
  useEffect(() => {
    client.get("/blog").then(({ data }) => setRows(data)).catch((e) => toast.error(apiErr(e)));
  }, []);
  if (!rows) return <Spinner label="Loading blog" />;
  if (!rows.length) return <div className="surface p-8 text-center text-sm text-slate-500" data-testid="blog-empty">Abhi koi blog post nahi hai.</div>;
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="blog-list">
      {rows.map((p) => (
        <button key={p.id} data-testid={`blog-card-${p.id}`} onClick={() => onOpen(p.slug)} className="surface text-left overflow-hidden group hover:border-gold/50 transition-colors duration-200">
          {p.image_url ? <img src={IMG(p.image_url)} alt="" className="w-full h-40 object-cover bg-black" /> : <div className="h-2 bg-gold/60" />}
          <div className="p-4">
            <div className="text-[10px] uppercase tracking-widest text-gold">{(p.tags || []).join(" · ") || "Insight"}</div>
            <h3 className="font-head text-white mt-1 group-hover:text-gold transition-colors duration-200">{p.title}</h3>
            <p className="text-xs text-slate-400 mt-1.5 line-clamp-2">{p.summary}</p>
            <div className="text-[11px] text-slate-500 mt-2 flex items-center gap-3"><span>{stamp(p.created_at)}</span><span className="flex items-center gap-1"><MessageSquare size={11} />{p.comments}</span>{p.published === false && <span className="text-bear">Draft</span>}</div>
          </div>
        </button>
      ))}
    </div>
  );
}

export function Blog() {
  const [slug, setSlug] = useState(null);
  return slug ? <BlogPost slug={slug} onBack={() => setSlug(null)} /> : <BlogList onOpen={setSlug} />;
}
